import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import FiMiniImage from '../../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../../assets/images/plp-fi-series-three-plus.png';
import { useLocale } from '../../../contexts/LocaleContext';
import useHardwareFirstPdpPricing from '../../../hooks/useHardwareFirstPdpPricing';
import { quizQuestionPage } from '../../../lib/analytics/events';
import { centsToDollarsNumber } from '../../../lib/util';
import * as types from '../../../types';
import styles from '../HardwareFirstQuiz.module.scss';
import { PRODUCT_DEVICE_KEY, ProductId } from '../rules';

const RESULT_PRODUCTS: Record<ProductId, { name: string; image: string; descriptionKey: string }> = {
  mini: { name: 'Fi Mini', image: FiMiniImage, descriptionKey: 'hf_quiz_result_desc_mini' },
  series3plus: { name: 'Fi Series 3+', image: FiSeries3PlusImage, descriptionKey: 'hf_quiz_result_desc_s3' },
};

interface ResultStepProps {
  recommendation: ProductId;
  onContinue(deviceKey: 'mini' | 's3'): void;
}

/** The recommendation, and the one CTA that acts on it. */
export default function ResultStep({ recommendation, onContinue }: ResultStepProps) {
  const { t } = useTranslation();
  const { wholeNumberCurrencyFormatter } = useLocale();
  const products = useSelector((state: types.AppState) => state.config.products);
  const product = RESULT_PRODUCTS[recommendation];
  const deviceKey = PRODUCT_DEVICE_KEY[recommendation];

  // Mirrors the PLP: the mini adds by its own sku, the series 3+ by its first
  // variant's, since band size and colour are chosen later.
  const miniProduct = products.find((p) => p.id === types.miniCollarId) as types.IMiniProduct | undefined;
  const s3Product = products.find((p) => p.id === types.series3PlusCollarId) as types.ICollarKitProduct | undefined;
  const sku = recommendation === 'mini' ? miniProduct?.sku : s3Product?.variants[0]?.sku;
  // Undefined while loading, when not enrolled, or when pricing fails; the row
  // is omitted rather than guessed, matching the PDP.
  const discount = useHardwareFirstPdpPricing(sku);

  useEffect(() => {
    quizQuestionPage.quizSuggestedDevice(deviceKey);
  }, [deviceKey]);

  return (
    <div className={styles.stepBody}>
      <div className={styles.resultShot}>
        <img className={styles.resultImage} src={product.image} alt={product.name} />
      </div>
      <div className={styles.resultEyebrow}>{t('hf_quiz_we_recommend')}</div>
      <h2 className={styles.stepTitle}>{product.name}</h2>
      <div className={styles.resultDescription}>{t(product.descriptionKey)}</div>
      {discount && (
        <div className={styles.resultPrice}>
          {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discount.discountedPriceInCents))}
        </div>
      )}
      <button
        type="button"
        className={styles.primaryCta}
        onClick={() => {
          quizQuestionPage.quizSuggestedDeviceShop();
          onContinue(deviceKey);
        }}
      >
        {t('hf_quiz_continue_with', { product: product.name })}
      </button>
    </div>
  );
}
